import { useCallback, useEffect, useRef } from "react";
import { SUSPENSE_SPIN_MS } from "../Components/Common/DefaultStyleWheel";
import { playSound, playSoundFitted, primeAudioDuration, stopSound } from "../utils/gameAudio";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const startFittedSpin = (soundsRef, spinRef) => {
  const cfg = soundsRef.current.spin_start;
  if (!cfg?.audio_url) return false;
  stopSound(spinRef.current);
  spinRef.current = playSoundFitted(cfg.audio_url, {
    volume: cfg.volume ?? 1,
    durationMs: SUSPENSE_SPIN_MS,
  });
  return Boolean(spinRef.current);
};

/**
 * Spin the Wheel sound cues for the prize-palette wheel.
 * Spin start is time-fitted to the suspense spin so it finishes as the wheel lands.
 * The winner track plays as the clap when the prize modal opens.
 */
export default function usePrizeWheelSpinAudio() {
  const soundsRef = useRef({});
  const spinRef = useRef(null);
  const clapRef = useRef(null);
  const awaitingSpinRef = useRef(false);

  const stopSpin = useCallback(() => {
    awaitingSpinRef.current = false;
    stopSound(spinRef.current);
    spinRef.current = null;
  }, []);

  const stopClap = useCallback(() => {
    stopSound(clapRef.current);
    clapRef.current = null;
  }, []);

  const playSpinStart = useCallback(() => {
    stopClap();
    stopSpin();
    awaitingSpinRef.current = true;
    if (startFittedSpin(soundsRef, spinRef)) awaitingSpinRef.current = false;
  }, [stopClap, stopSpin]);

  const playWinnerClap = useCallback(() => {
    stopSpin();
    stopClap();
    const cfg = soundsRef.current.winner;
    if (!cfg?.audio_url) return;
    clapRef.current = playSound(cfg.audio_url, {
      volume: cfg.volume ?? 1,
      loop: false,
    });
  }, [stopClap, stopSpin]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/game-sounds/spin_wheel`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data?.success) return;
        const map = {};
        (data.sounds || []).forEach((sound) => {
          if (sound.enabled && sound.audio_url) map[sound.action_key] = sound;
        });
        soundsRef.current = map;
        if (map.spin_start?.audio_url) primeAudioDuration(map.spin_start.audio_url);
        if (map.winner?.audio_url) primeAudioDuration(map.winner.audio_url);
        if (awaitingSpinRef.current && startFittedSpin(soundsRef, spinRef)) {
          awaitingSpinRef.current = false;
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => () => {
    stopSound(spinRef.current);
    stopSound(clapRef.current);
    spinRef.current = null;
    clapRef.current = null;
  }, []);

  return { playSpinStart, stopSpin, playWinnerClap };
}
